import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote, Undo, Redo, Code, Minus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Editorial rich-text editor (TipTap) — emits HTML.
 * Props: value (html), onChange(html), placeholder, minHeight, testId
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Write…", minHeight = 220, testId = "rich-editor" }) {
  const { headers } = useAuth();
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener", target: "_blank" } }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "tiptap-prose focus:outline-none",
        style: `min-height:${minHeight}px;`,
        "data-testid": `${testId}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Keep editor in sync when value changes externally (e.g. opening a different record)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const btn = (active, onClick, Icon, label) => (
    <button
      type="button"
      title={label}
      onClick={onClick}
      data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`p-2 hover:bg-graphite/8 transition-colors ${active ? "text-[#1E3668] bg-graphite/5" : "text-graphite/75"}`}
    >
      <Icon size={15} />
    </button>
  );

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="hairline bg-warmwhite" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 hairline-bottom px-2 py-1.5">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic")}
        <span className="w-px h-5 bg-graphite/15 mx-1" />
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading 2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Heading 3")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote")}
        <span className="w-px h-5 bg-graphite/15 mx-1" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bullets")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered")}
        {btn(editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run(), Code, "Code")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Divider")}
        <span className="w-px h-5 bg-graphite/15 mx-1" />
        {btn(editor.isActive("link"), addLink, LinkIcon, "Link")}
        {btn(false, () => fileRef.current?.click(), ImageIcon, "Image")}
        <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid={`${testId}-file`} />
        <span className="w-px h-5 bg-graphite/15 mx-1" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo, "Undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo, "Redo")}
      </div>
      <div className="px-4 py-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
